import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { createLowlight } from 'lowlight'
import javascript from 'highlight.js/lib/languages/javascript'
import { Editor } from '@tiptap/react'
import { Node } from 'unist'
import { Node as PMNode } from '@tiptap/pm/model'
import { ChangeCollector } from '../../utils/ChangeCollector'
import { AIText } from './aiText'
import { unified } from 'unified'
import remarkParse from 'remark-parse'
import remarkGfm from 'remark-gfm'
import remarkHtml from 'remark-html'

// 创建语法高亮实例
const lowlight = createLowlight();
// 注册 JavaScript 语言
lowlight.register({ javascript });

// 全局单例ChangeCollector实例，用于AST处理
const changeCollector = new ChangeCollector();

// 创建 remark 处理器
const remarkProcessor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkHtml, { sanitize: false }); // 不要过滤HTML标签

// 创建一个处理 Markdown 粘贴的扩展
const CustomPasteHandler = Extension.create({
  name: 'customPasteHandler',
  
  addProseMirrorPlugins() {
    const { editor } = this;
    
    return [
      new Plugin({
        key: new PluginKey('customPasteHandler'),
        props: {
          // 处理粘贴事件
          handleDOMEvents: {
            paste: (_, event) => {
              console.log('DOM paste event captured');
              const { clipboardData } = event;
              if (!clipboardData) {
                console.log('剪贴板数据为空');
                return false;
              }
              
              // 打印所有可用的剪贴板格式
              console.log('可用的剪贴板格式:', clipboardData.types);
              
              // 尝试获取不同格式的内容
              const textPlain = clipboardData.getData('text/plain');
              
              if (!textPlain) {
                console.log('无法获取任何文本内容');
                return false;
              }
              
              // 阻止默认粘贴行为
              event.preventDefault();
              
              // 使用async/await处理粘贴内容
              (async () => {
                try {
                  // 使用 remark 将 Markdown 转换为 HTML
                  const file = await remarkProcessor.process(textPlain);
                  const processedHtml = String(file);
                  
                  console.log('处理后的HTML:', processedHtml.substring(0, 100) + '...');
                  
                  // 将处理后的 HTML 插入编辑器
                  editor.commands.insertContent(processedHtml);
                  console.log('内容插入成功');
                } catch (error) {
                  console.error('Markdown 转换失败:', error);
                  // 转换失败时作为普通文本插入
                  editor.commands.insertContent(textPlain);
                }
              })();
              
              return true;
            }
          }
        }
      })
    ];
  }
});

// 创建一个简化的 AST 类型
interface MarkdownAST extends Node {
  type: string;
  value?: string;
  children?: MarkdownAST[];
  data?: Record<string, unknown>;
}

// 扩展Storage类型，以便TypeScript能够识别添加的属性
declare module '@tiptap/core' {
  interface ExtensionStorage {
    markdown: {
      _cachedAST: MarkdownAST | null;
      _lastSize: number;
      generateAST: (editor: Editor) => MarkdownAST;
      getMarkdownFromAST: (ast: MarkdownAST) => string;
      getMarkdown: (editor: Editor) => string;
      getCurrentAST: () => MarkdownAST | null;
    }
  }
}

// 创建Markdown导出扩展
const MarkdownExport = Extension.create({
  name: 'markdownExport',
  
  // 添加全局方法
  addGlobalAttributes() {
    return [
      {
        types: ['doc'],
        attributes: {
          exportMarkdown: {
            default: true,
          },
        },
      },
    ]
  },
  
  // 添加存储方法而不是命令
  addStorage() {
    // 使用闭包来存储局部变量
    let cachedAST: MarkdownAST | null = null;
    let lastSize = 0;
    
    // 创建本地函数，不依赖 this
    const generateASTLocal = (editor: Editor): MarkdownAST => {
      try {
        // 获取编辑器的根节点
        const state = editor.state;
        const docNode = state.doc;
        
        // 创建根节点
        const ast: MarkdownAST = {
          type: 'root',
          children: []
        };
        
        // 遍历文档的每个子节点，忽略不需要的参数
        docNode.forEach((node) => {
          // 根据节点类型构建 AST
          const nodeAST = createNodeAST(node);
          if (nodeAST) {
            ast.children?.push(nodeAST);
          }
        });
        
        // 缓存生成的 AST，以便快速访问
        cachedAST = ast;
        lastSize = editor.state.doc.content.size;
        return ast;
      } catch (error) {
        console.error('AST 生成错误:', error);
        return { type: 'root', children: [] };
      }
    };
    
    const getMarkdownFromASTLocal = (ast: MarkdownAST): string => {
      try {
        return renderAST(ast);
      } catch (error) {
        console.error('从 AST 生成 Markdown 错误:', error);
        return '';
      }
    };
    
    return {
      // 生成 Markdown AST
      generateAST: generateASTLocal,
      
      // 从 AST 生成 Markdown
      getMarkdownFromAST: getMarkdownFromASTLocal,
      
      // 获取 Markdown 内容（主方法）
      getMarkdown: (editor: Editor): string => {
        try {
          // 检查是否有缓存的 AST，如果编辑器内容已更改，则重新生成
          if (!cachedAST || editor.state.doc.content.size !== lastSize) {
            generateASTLocal(editor);
          }
          
          // 如果有缓存的 AST，使用它生成 Markdown
          if (cachedAST) {
            return getMarkdownFromASTLocal(cachedAST);
          }
          
          // 如果AST生成失败，使用HTML内容并通过ChangeCollector处理
          const html = editor.getHTML();
          const { markdown } = changeCollector.processContent(html);
          return markdown;
        } catch (error) {
          console.error('Markdown导出错误:', error);
          return '';
        }
      },
      
      // 获取当前的 AST（用于存储在文档中）
      getCurrentAST: function() {
        return cachedAST;
      },
      
      // 保持与接口兼容的公共属性
      _cachedAST: null as MarkdownAST | null,
      _lastSize: 0
    };
  }
});

// 辅助函数：创建节点的 AST 表示
function createNodeAST(node: PMNode): MarkdownAST | null {
  // 根据节点类型创建不同的 AST 结构
  switch (node.type.name) {
    case 'heading': {
      const level = node.attrs.level || 1;
      const headingText = node.textContent;
      return {
        type: 'heading',
        data: { level },
        children: [{ type: 'text', value: headingText }]
      };
    }
    case 'paragraph': {
      return {
        type: 'paragraph',
        children: [{ type: 'text', value: node.textContent }]
      };
    }
    case 'aiText': {
      // 处理AI文本节点
      return {
        type: 'html',
        value: `<span class="ai-gradient-text">${node.textContent}</span>`
      };
    }
    case 'bulletList': {
      return {
        type: 'list',
        data: { ordered: false },
        children: []
      };
    }
    case 'orderedList': {
      return {
        type: 'list',
        data: { ordered: true },
        children: []
      };
    }
    case 'listItem': {
      return {
        type: 'listItem',
        children: [{ type: 'text', value: node.textContent }]
      };
    }
    case 'taskList': {
      return {
        type: 'list',
        data: { task: true },
        children: []
      };
    }
    case 'taskItem': {
      return {
        type: 'listItem',
        data: { checked: node.attrs.checked },
        children: [{ type: 'text', value: node.textContent }]
      };
    }
    case 'codeBlock': {
      const lang = node.attrs.language || '';
      return {
        type: 'code',
        data: { language: lang },
        value: node.textContent
      };
    }
    case 'blockquote': {
      const quoteContent = node.textContent;
      return {
        type: 'blockquote',
        children: [{ type: 'text', value: quoteContent }]
      };
    }
    case 'horizontalRule': {
      return {
        type: 'thematicBreak'
      };
    }
    case 'image': {
      return {
        type: 'image',
        data: { 
          url: node.attrs.src,
          alt: node.attrs.alt || ''
        }
      };
    }
    case 'table': {
      return {
        type: 'table',
        children: []
      };
    }
    default: {
      // 对于其他类型，尝试提取文本内容
      if (node.textContent) {
        return {
          type: 'text',
          value: node.textContent
        };
      }
      return null;
    }
  }
}

// 辅助函数：从 AST 渲染 Markdown
function renderAST(ast: MarkdownAST): string {
  if (!ast) return '';
  
  switch (ast.type) {
    case 'root': {
      return ast.children?.map(child => renderAST(child)).join('\n\n') || '';
    }
    case 'heading': {
      // 修复类型问题
      const levelValue = ast.data?.level;
      const level = typeof levelValue === 'number' ? levelValue : 1;
      const headingText = ast.children?.map(child => renderAST(child)).join('') || '';
      return '#'.repeat(level) + ' ' + headingText;
    }
    case 'paragraph': {
      return ast.children?.map(child => renderAST(child)).join('') || '';
    }
    case 'text': {
      return ast.value || '';
    }
    case 'list': {
      const isOrdered = ast.data?.ordered;
      const isTask = ast.data?.task;
      return (ast.children || []).map((item, index) => {
        let prefix = isOrdered ? `${index + 1}. ` : '- ';
        if (isTask) {
          const checked = item.data?.checked;
          prefix = checked ? '- [x] ' : '- [ ] ';
        }
        return prefix + renderAST(item);
      }).join('\n');
    }
    case 'listItem': {
      return ast.children?.map(child => renderAST(child)).join('') || '';
    }
    // case 'code': {
    //   const lang = ast.data?.language || '';
    //   return '```' + lang + '\n' + (ast.value || '') + '\n```';
    // }
    case 'blockquote': {
      const quoteContent = ast.children?.map(child => renderAST(child)).join('') || '';
      return '> ' + quoteContent;
    }
    case 'thematicBreak': {
      return '---';
    }
    case 'image': {
      return `![${ast.data?.alt || ''}](${ast.data?.url || ''})`;
    }
    case 'table': {
      // 简化的表格实现
      return '[表格内容]';
    }
    case 'html': {
      // 直接输出HTML内容
      return ast.value || '';
    }
    default: {
      return '';
    }
  }
}

// 配置Markdown扩展
export const markdownExtensions = [
  // 自定义粘贴处理扩展
  CustomPasteHandler,
  
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3, 4, 5]
    },
    bulletList: {
      keepMarks: true,
      keepAttributes: true,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: true,
    },
    codeBlock: false, // 禁用默认的代码块，使用自定义的
    // 简化粘贴规则相关配置
    bold: {},
    italic: {},
    code: {},
    dropcursor: {},
    gapcursor: undefined,
  }),
  
  // 自定义代码块
  CodeBlockLowlight.configure({
    lowlight,
    HTMLAttributes: {
      class: 'hljs code-block',
    },
  }),
  
  // 图片扩展
  Image.configure({
    HTMLAttributes: {
      class: 'editor-image',
    },
  }),
  
  // 链接扩展 - 移除linkOnPaste配置
  Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      class: 'editor-link',
    },
  }),
  
  // AI文本扩展
  AIText,
  
  // 任务列表
  TaskList.configure({
    HTMLAttributes: {
      class: 'task-list',
    },
  }),
  TaskItem.configure({
    nested: true,
    HTMLAttributes: {
      class: 'task-item',
    },
  }),
  
  // Markdown导出
  MarkdownExport,
  
  // 其他扩展
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableHeader,
  TableCell,
];